import { authSplitApi } from 'redux/helpers/slice.helpers';
import { EResidencyUrls } from 'data/urls/residency.url';
import {
  TChangeResidencyFormDataUI,
  TCreateTenantResidencyUI,
  TResidencyAnimalCountUI,
  TResidencyByApplicationDataUI,
  TResidencyData,
  TResidencyDataUI,
  TResidencyWithAnimalsDataDB,
  TValidateResidencyUnitUI,
} from 'data/types/residency.types';
import {
  residencyAnimalApplicationsConverter,
  residencyAnimalCountConverter,
  residencyDataConverter,
  tenantResidencyCreateConverter,
  updateResidencyDataConverter,
} from 'data/convertors/residency.convertor';
import { TMoveOutFromResidencyFormUI } from 'data/types/animal.types';
import { EAnimalUrls } from 'data/urls/animal.url';
import { animalMoveOutFromResidenciesConvertor } from 'data/convertors/animal.convertors';
import { TReapplyApplicationUI } from 'data/types/application.types';
import { reapplyApplicationConverter } from 'data/convertors/application.converters';
import { animalApi } from 'redux/apiSlice/animal.slice';

const baseUrl = `${EResidencyUrls.RESIDENCY}`;

export const residencyApi = authSplitApi('residency', [
  'residency',
  'inCompleteResidency',
  'inCompleteResidencyApplication',
  'residency-with-animals',
]).injectEndpoints({
  endpoints: build => ({
    getResidencyData: build.query<TResidencyDataUI[], null | void>({
      query() {
        return {
          url: `${baseUrl}/${EResidencyUrls.CARD}`,
          method: 'GET',
        };
      },
      providesTags: ['residency'],
      transformResponse: (data: TResidencyData[]) =>
        data.map(item => residencyDataConverter.fromDb(item)),
    }),
    getIncompleteResidencyData: build.query<TResidencyDataUI[], void>({
      query() {
        return {
          url: `${baseUrl}/${EResidencyUrls.INCOMPLETE}`,
          method: 'GET',
        };
      },
      providesTags: ['inCompleteResidency'],
      transformResponse: (data: TResidencyData[]) =>
        // TODO: sort by priority of incompleted onboarding state
        data.map(item => residencyDataConverter.fromDb(item)),
    }),
    createTenantResidency: build.mutation<string, TCreateTenantResidencyUI>({
      query(data) {
        return {
          url: `${baseUrl}/${EResidencyUrls.APPLICATION}`,
          method: 'POST',
          body: tenantResidencyCreateConverter.toDb(data),
        };
      },
    }),
    moveOutAnimalsFromResidency: build.mutation<
      void,
      TMoveOutFromResidencyFormUI
    >({
      query(body) {
        return {
          url: `${baseUrl}/${EAnimalUrls.ANIMALS}/${EAnimalUrls.MOVE_OUT}`,
          method: 'PATCH',
          body: animalMoveOutFromResidenciesConvertor.toDb(body),
        };
      },
      invalidatesTags: ['inCompleteResidency'],
    }),

    validateUnitNumber: build.mutation<string, TValidateResidencyUnitUI>({
      query(data) {
        return {
          url: `${baseUrl}/${EResidencyUrls.APPLICATION}/${data.residencyApplicationId}/${EResidencyUrls.UNIT}/${EResidencyUrls.VERIFY}`,
          method: 'PATCH',
          body: data,
        };
      },
    }),
    updateResidencyData: build.mutation<void, TChangeResidencyFormDataUI>({
      query(body) {
        return {
          url: `${baseUrl}`,
          method: 'PUT',
          body: updateResidencyDataConverter.toDb(body),
        };
      },
      // invalidatesTags: ['residency'], check if needed
    }),
    setResidencyFirstFaceDate: build.mutation<
      void,
      { tenantResidencyId: string }
    >({
      query(body) {
        return {
          url: `${baseUrl}/${EResidencyUrls.FIRST_TIME_FACE}`,
          method: 'PATCH',
          body,
        };
      },
    }),

    residancyAnimalCount: build.mutation<void, TResidencyAnimalCountUI>({
      query(data) {
        return {
          url: `${baseUrl}/${EResidencyUrls.ANIMAL_COUNT}`,
          method: 'PATCH',
          body: residencyAnimalCountConverter.toDb(data),
        };
      },
      invalidatesTags: ['inCompleteResidencyApplication'],
    }),

    getResidencyWithAnimals: build.query<
      TResidencyByApplicationDataUI[],
      null | void
    >({
      query() {
        return {
          url: `${baseUrl}`,
          method: 'GET',
        };
      },
      providesTags: ['residency-with-animals'],
      transformResponse: (data: TResidencyWithAnimalsDataDB[]) =>
        residencyAnimalApplicationsConverter.fromDb(data),
    }),
    reapplyApplication: build.mutation<void, TReapplyApplicationUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EResidencyUrls.ANIMALS}/${EResidencyUrls.REAPPLY}`,
          method: 'PATCH',
          body: reapplyApplicationConverter.toDb(body),
        };
      },
      invalidatesTags: ['residency-with-animals'],
      // Same reasoning as retract: the animal lists and the completion banner live in animalApi.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          animalApi.util.invalidateTags([
            'animal',
            'applicationAnimal',
            'animals',
            'incompleteAnimals',
          ]),
        );
      },
    }),
  }),
});

export const {
  useGetResidencyDataQuery,
  useCreateTenantResidencyMutation,
  useGetIncompleteResidencyDataQuery,
  useLazyGetIncompleteResidencyDataQuery,
  useMoveOutAnimalsFromResidencyMutation,
  useValidateUnitNumberMutation,
  useUpdateResidencyDataMutation,
  useSetResidencyFirstFaceDateMutation,
  useResidancyAnimalCountMutation,
  useGetResidencyWithAnimalsQuery,
  useReapplyApplicationMutation,
} = residencyApi;
